// frontend/src/lib/queryCache.ts
//
// Basit in-memory TTL cache + istek de-duplikasyonu.
// Aynı endpoint'e yapılan eşzamanlı istekleri tek bir isteğe indirger
// ve sonucu belirli bir süre boyunca önbellekte tutar.

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

class QueryCache {
  private cache = new Map<string, CacheEntry<any>>();
  private pendingRequests = new Map<string, Promise<any>>();

  /** Önbellekten veri al (süresi dolmuşsa null döner) */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }
    return entry.data as T;
  }

  /** Veriyi TTL ile önbelleğe kaydet */
  set<T>(key: string, data: T, ttlMs: number): void {
    this.cache.set(key, {
      data,
      expiresAt: Date.now() + ttlMs,
    });
  }

  /**
   * Eşzamanlı istekleri de-duplike eder:
   * 1. Önbellekte varsa anında döner
   * 2. Aynı key için zaten bir istek devam ediyorsa onun Promise'ini paylaşır
   * 3. Yoksa yeni istek ateşler, sonucu önbelleğe alır
   */
  async dedupe<T>(key: string, fetcher: () => Promise<T>, ttlMs: number): Promise<T> {
    // 1. Önbellek kontrolü
    const cached = this.get<T>(key);
    if (cached !== null) return cached;

    // 2. Devam eden istek kontrolü
    const pending = this.pendingRequests.get(key);
    if (pending) return pending as Promise<T>;

    // 3. Yeni istek
    const promise = fetcher()
      .then((data) => {
        this.set(key, data, ttlMs);
        this.pendingRequests.delete(key);
        return data;
      })
      .catch((err) => {
        this.pendingRequests.delete(key);
        throw err;
      });

    this.pendingRequests.set(key, promise);
    return promise;
  }

  /** Belirli bir key veya prefix ile eşleşen girişleri temizle */
  invalidate(keyOrPrefix: string): void {
    if (this.cache.has(keyOrPrefix)) {
      this.cache.delete(keyOrPrefix);
      return;
    }
    // Prefix eşleştirme
    for (const key of this.cache.keys()) {
      if (key.startsWith(keyOrPrefix)) {
        this.cache.delete(key);
      }
    }
  }

  /** Tüm önbelleği temizle */
  clear(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }
}

export const queryCache = new QueryCache();

/** Önbellek TTL sabitleri (milisaniye) */
export const CACHE_TTL = {
  /** Kurs listesi — nadiren değişir */
  COURSES: 5 * 60 * 1000,
  /** Tek kurs detayı */
  COURSE_DETAIL: 5 * 60 * 1000,
  /** Kurs ders listesi (özet alanlar) */
  LESSONS_LIST: 5 * 60 * 1000,
  /** Liderlik tablosu — biraz daha dinamik */
  LEADERBOARD: 60 * 1000,
  /** Dashboard verisi */
  DASHBOARD: 2 * 60 * 1000,
  /** Isı haritası — geçmiş veri, sık değişmez */
  HEATMAP: 10 * 60 * 1000,
  /** Kullanıcı özellikleri (feature flags) */
  FEATURES: 10 * 60 * 1000,
} as const;
